import { createRouter, createWebHistory } from "vue-router";
import { build } from "./version.json";
import { ApplicationRequiredError } from "./core/graphql-api.js";

import AppLayoutV2 from "./v2_template/app/AppLayout.vue";
import HomeV2 from "./v2_template/views/home/Home.vue";
import ProjectsV2 from "./v2_template/views/projects/Projects.vue";
import ProjectV2 from "./v2_template/views/project/Project.vue";
import DocsV2 from "./v2_template/views/docs/Docs.vue";
import DocumentV2 from "./v2_template/views/document/Document.vue";
import SearchV2 from "./v2_template/views/search/Search.vue";
import ActivityV2 from "./v2_template/views/activity/Activity.vue";
import ApplicationView from "./views/ApplicationView.vue";
import No_access from "./views/auth/no_access.vue";
import { BROADCAST_MESSAGE, WARN_TOAST } from "./core/constants.js";
import Welcome from "./views/auth/Welcome.vue";
import AccountSettings from "./views/common/AccountSettings.vue";
import LogIn from "./views/auth/LogIn.vue";
import WaitList from "./views/auth/WaitList.vue";
import OAuth2StorageProviderCallback from "./views/auth/auth-callbacks/OAuth2StorageProviderCallback.vue";
import {Repo} from "@automerge/automerge-repo";
import {BroadcastChannelNetworkAdapter} from "@automerge/automerge-repo-network-broadcastchannel";
import {BrowserWebSocketClientAdapter} from "@automerge/automerge-repo-network-websocket";
import {IndexedDBStorageAdapter} from "@automerge/automerge-repo-storage-indexeddb";
import Store from "./store/index.js";
import {toRaw} from "vue";
import SubscriptionSettings from './views/common/SubscriptionSettings.vue'
import ProfileService from "./service/profileService.js";
import ErrorPage from './views/auth/Error.vue';

const {
  VITE_AUTOMERGE_SYNC_SERVER: automergeUrl,
} = import.meta.env;

const routes = [
  {
    path: "/signup",
    name: "signup",
    redirect: (to) => ({ path: "/login", query: { to: to.name } }),
    meta: {
      requireAuth: false,
    },
  },
  {
    path: "/login",
    name: "login",
    component: LogIn,
    meta: {
      requireAuth: false,
    },
  },
  {
    path: "/welcome",
    name: "welcome",
    component: Welcome,
    meta: {
      requireAuth: false,
    },
  },
  {
    path: "/wait-list",
    name: "wait-list",
    component: WaitList,
    meta: {
      requireAuth: false,
    },
  },
  {
    path: "/error-page",
    name: "error-page",
    component: ErrorPage,
    meta: {
      requireAuth: false,
    },
  },
  {
    path: "/google_oauth2_callback",
    name: "google_oauth2_callback",
    component: OAuth2StorageProviderCallback,
    meta: {
      requireAuth: false,
    },
  },
  {
    path: "/ms_oauth2_callback",
    name: "ms_oauth2_callback",
    component: OAuth2StorageProviderCallback,
    meta: {
      requireAuth: false,
    },
  },
  {
    path: "/box_oauth2_callback",
    name: "box_oauth2_callback",
    component: OAuth2StorageProviderCallback,
    meta: {
      requireAuth: false,
    },
  },
  {
    path: "/dropbox_oauth2_callback",
    name: "dropbox_oauth2_callback",
    component: OAuth2StorageProviderCallback,
    meta: {
      requireAuth: false,
    },
  },
  {
    // v2 is now the main app — it owns all the root-level paths that used to
    // collide with the legacy views under ApplicationView below (/, /search,
    // /p/:projectId, /p/:projectId/d/:id). Those legacy routes were removed
    // rather than the files deleted, so this stays reversible.
    path: "/",
    component: AppLayoutV2,
    meta: { requireAuth: true },
    children: [
      { path: "",                      name: "v2-home",     component: HomeV2 },
      { path: "search",                name: "v2-search",   component: SearchV2 },
      { path: "activity",              name: "v2-activity", component: ActivityV2 },
      { path: "projects",              name: "v2-projects", component: ProjectsV2 },
      { path: "docs",                  name: "v2-docs",     component: DocsV2 },
      { path: "p/:projectId",          name: "v2-project",  component: ProjectV2 },
      { path: "p/:projectId/d/:docId", name: "v2-document", component: DocumentV2 },
    ],
  },
  {
    // No v2 equivalent yet for these two, so they stay on the legacy layout.
    // Path is otherwise unused/unvisited directly — children below still
    // resolve at their own absolute paths regardless of this parent's path.
    path: "/legacy",
    name: "application-view",
    component: ApplicationView,
    children: [
      {
        path: "/account-settings",
        name: "account-settings",
        component: AccountSettings,
        meta: {
          requireAuth: true,
        },
      },
      {
        path: "/subscriptions",
        name: "subscriptions",
        component: SubscriptionSettings,
        meta: {
          requireAuth: true,
        },
      }
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    component: No_access,
    meta: {
      requireAuth: false,
    },
  }

];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

export default {
  install(app, options) {
    const {
      globalProperties: { $auth0, $fetch },
    } = app.config;
    let updateStarted = false;
    router.install(app);

    router.beforeEach(async (to, from, next) => {
      // console.log("ROUTER", to.path, to.meta);
      await $auth0.init();
      if (to.meta.requireAuth === false) return next();

      try {
        if (to.params.projectId)
          to.params.projectId = to.params.projectId.toLowerCase();
        if (to.params.documentId)
          to.params.documentId = to.params.documentId.toLowerCase();
        if (to.params.docId)
          to.params.docId = to.params.docId.toLowerCase();
        // console.log(to.params);
      } catch (e) {
        console.error(e.message);
      }

      const { code, state } = to.query;
      if (to.path === "/" && code && state) {
        try {
          localStorage.setItem("build", `${build}`);
          await $auth0.signInRedirectCallback(to.fullPath);
          const fallbackPath = localStorage.getItem("fallbackPath");
          localStorage.removeItem("fallbackPath");
          if (fallbackPath) {
            try {
              const { projectId, documentId } = JSON.parse(fallbackPath);
              if (projectId && documentId) {
                return next(`/p/${projectId}/d/${documentId}`);
              } else if (projectId && !documentId) {
                return next(`/p/${projectId}`);
              }
            } catch (e) {
              console.log("warning:", e.message);
            }
          }
        } catch (e) {
          console.log("warning:", e.message);
        }
        return next("/");
      }

      const user = await $auth0.getUser();
      if (!user.isAuthenticated) {
        const { projectId, documentId } = to.params;
        if (projectId) {
          // prettier-ignore
          localStorage.setItem("fallbackPath", JSON.stringify({ projectId, documentId }));
          return await $fetch
            .inputSyncUser()
            .then(() => $fetch.inputSyncProject(true, projectId))
            .then(() => next())
            .catch(() => next("/login"));
        }
        return next("/login");
      } else if (!user.profile.email_verified) {        
        return next("/welcome");
      }
      let errorCode = null;
      try {
          if (!localStorage.getItem("profileChecked")) {            
            const profileService = new ProfileService();
            const {
              name = null,
              email = "",
              profilePicture = "",
              profileStatus,
              status
            } = await profileService.getMyProfile();
            localStorage.setItem("profileChecked", "yes");
            localStorage.setItem("profileStatus", profileStatus);
            console.log("need to ",status);
            
            if (status === 401) {
              errorCode = 401
              await $auth0.signOut();
              return;
            }
            
          }
          
          // TODO handle this properly. Possible status are APPROVED, WAITLIST and BLOCKED. 
          // BLOCKED users should be sent to an error page. See FOLIA-126
          if (localStorage.getItem("profileStatus") != 'APPROVED') {
            return next("/wait-list");
          }

        // This is hack to make sure automerge get auth token.
        const adaptor = toRaw(Store.getters.automergeNetworkAdaptor)
        const networkUrl = automergeUrl + "?Authentication=" + localStorage.getItem("accessToken");
        adaptor.url = networkUrl;
      } catch (e) {
        // if (e.name === ApplicationRequiredError.name) {
        //   // Wrong logic. Send all to an error page
        //   return next("/wait-list");
        // } else {
        //   if (to.path === "/welcome") return next();
        //   return next("/welcome");
        // }  
        if(errorCode === 401){
          return next(`/error-page?error=${401}`);
        }
              
        const errorMessage = encodeURIComponent(JSON.stringify(e));  
        return next(`/error-page?error=${errorMessage}`);
      }

      const storedBuild = localStorage.getItem("build");
      if (storedBuild && storedBuild !== `${build}`) {
        if (updateStarted) return;
        updateStarted = true;

        localStorage.setItem("build", `${build}`);
        const restartTimeout = 5000;
        $fetch.dispatch(BROADCAST_MESSAGE.TOAST, {
          type: WARN_TOAST,
          title: "UPDATE",
          message: `A new version of the application has been found, so all user data will be removed and the page will reload after all outgoing sync operations.`,
          close_timeout: restartTimeout,
        });
        await $auth0.signOut();
        await new Promise((resolve) => setTimeout(resolve, restartTimeout));
      }

      next();
    });
  },
};
